<script lang="ts">
  import { ThreeItemKeys, ThreeItemTypes } from '../../constants/three'
  import usePrinterQueries from '../../routes/project/hooks/usePrinterQueries'
  import Pane from '../pane/Pane.svelte'
  import ContextMenu from '../context-menu/ContextMenu.svelte'
  import ContextMenuTrigger from '../context-menu/ContextMenuTrigger.svelte'
  import PrinterContextMenuContent from '../printer-context-menu-content/PrinterContextMenuContent.svelte'
  import PrinterPaneHead from './components/PrinterPaneHead.svelte'
  import PrinterFileList from './components/PrinterFileList.svelte'
  import useCanvasSelection from '../../hooks/canvas-selection'
  import RenameForm from './components/RenameForm.svelte'
  import useObjectQueries from '../../routes/project/hooks/useObjectQueries'
  import useObjectsQueries from '../../routes/project/hooks/useObjectsQueries'
  import type { Object3D } from 'three'
  import { canvasSelection } from '../../stores/canvas-selection'
  import { getPrinterColor } from '../../helpers/printer'
  import { queryReactiveAccessor } from '../../helpers/tanstack-query'

  let { id } = $props<{ id: string }>()

  const selection = useCanvasSelection()
  const { useObject, hideMutation, showMutation, focusCamera, renameMutation }
    = useObjectQueries()
  const { useObjects } = useObjectsQueries()
  const { changeColorMutation } = usePrinterQueries()

  /* svelte-ignore state_referenced_locally */
  const printerQuery = useObject(id)
  const allObjectsQuery = useObjects()

  // Local UI state
  let editing = $state(false)

  // Derived values from query data - track dataUpdatedAt for reactivity
  const printer = $derived.by(queryReactiveAccessor(printerQuery))

  const visible = $derived.by(
    queryReactiveAccessor(printerQuery, data => data?.visible),
  )
  const selected = $derived.by(
    queryReactiveAccessor(
      printerQuery,
      data => !!data && $canvasSelection?.uuid === data.uuid,
    ),
  )
  const color = $derived.by(
    queryReactiveAccessor(printerQuery, data => getPrinterColor(data)),
  )
  const isEmpty = $derived.by(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    allObjectsQuery.dataUpdatedAt
    const printerId = printer?.userData[ThreeItemKeys.PRINTER_ID]
    if (!printerId) return true

    return !(allObjectsQuery.data ?? []).some(
      item =>
        item.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
          && item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId,
    )
  })

  // Handlers
  function toggleHidden() {
    if (!printer) return

    if (!printer.visible) {
      showMutation.mutate(id)
    }
    else {
      hideMutation.mutate(id)
    }
  }

  function addPrinterToSelection(event: MouseEvent, printer: Object3D) {
    if (!printer) return
    selection.upsert(printer, event.ctrlKey || event.metaKey)
  }

  async function handleRename(name: string): Promise<void> {
    await renameMutation.mutateAsync({ objectUuid: id, name })
  }

  function handleNameClick(e: MouseEvent) {
    if (!printer) return
    addPrinterToSelection(e, printer as Object3D)
  }

  function handleNameDoubleClick(e: MouseEvent) {
    if (!printer) return
    focusCamera(id)
    addPrinterToSelection(e, printer as Object3D)
  }
</script>

{#if printer}
  <Pane defaultValue={true} key={`printer-${id}`}>
    <ContextMenu>
      <ContextMenuTrigger>
        <PrinterPaneHead
          onColorChange={color =>
            changeColorMutation.mutateAsync({ objectUuid: id, color })}
          onToggleHidden={toggleHidden}
          {color}
          {isEmpty}
          {selected}
          {visible}
        >
          {#if editing}
            <RenameForm
              name={`printer-${printer.id}`}
              defaultValue={printer.name}
              onRename={handleRename}
              onSuccess={() => (editing = false)}
            />
          {:else}
            <button
              class="name"
              onclick={handleNameClick}
              ondblclick={handleNameDoubleClick}
            >
              {printer.name}
            </button>
          {/if}
        </PrinterPaneHead>
      </ContextMenuTrigger>

      <PrinterContextMenuContent
        onRename={() => (editing = true)}
        printerId={id}
      />
    </ContextMenu>

    <PrinterFileList printerId={printer.userData[ThreeItemKeys.PRINTER_ID]} />
  </Pane>
{/if}

<style lang="scss">
  .name {
    background-color: transparent;
    border: none;
    outline: none;
    color: currentColor;
    min-height: 3rem;
    width: 100%;
    text-align: start;
    padding: 0;
    cursor: pointer;

    &:focus-visible {
      outline: 1px solid var(--stroke);
      outline-offset: -1px;
    }
  }
</style>
